import ProjectSelect from "Dashboard/scenes/Projects/components/ProjectSelect";
import { useAppDispatch } from "hooks/hooks";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { ICreateChargeRequestParams } from "../../../domain/ICreateChargeRequestParams";
import {
  averageConsumption,
  averageConsumptionSuffix,
  haveAverageConsumption,
  haveUsage,
  unitName,
  unitNameLabel,
} from "../../../helpers/resourceUtil";
import {
  selectResourceDetails,
  updateLastKnownUsageAmount,
  updateResourceCharged
} from "../../Details/details.slice";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { setResourceChargePopoverState } from "../charges.slice";
import { resourcesServices } from "../../../services/resource.service";
import EmployeeSelect from "../../../components/EmployeeSelect";
import { styles } from "../../../../../../components/Popover/popoverSelectStyle";
import classes from "../../../../../../components/Popover/popover.module.scss";
import NumberInput from "Dashboard/components/NumberInput";
import { FormatNumber } from "utils/numberUtils";
import { RESOURCES_MODULE_NAME } from "Dashboard/scenes/Resources/domain/ResourcesModule";
import { hasFullAccessTo } from "utils/authUtil";
import ToolboxButton, { ToolboxButtonIcon } from "Dashboard/components/Buttons/ToolboxButton";
import DatePicker from "react-datepicker";
import { DiffDays } from "utils/dateUtils";
import { IOption } from "Dashboard/domain/IOption";

interface IProps {
  onCharge: VoidFunction;
  employees: IOption[];
}

const ResourceChargePopover = ({ onCharge, employees }: IProps) => {
  const dispatch = useAppDispatch();
  const resourceDetails = useSelector(selectResourceDetails);
  const { user } = useSelector(selectStateAuth);
  const [clickedTimes, setClickedTimes] = useState(0);
  const [incorrectUsage, setIncorrectUsage] = useState(false);
  const [usageEntered, setUsageEntered] = useState(0);
  const [quantityEntered, setQuantityEntered] = useState(0);
  const [chargeReportSequence, setChargeReportSequence]
    = useState<number | undefined>(undefined);
  const chargeReportSequenceString =
    chargeReportSequence !== undefined && chargeReportSequence !== null
      ? chargeReportSequence.toString() : "";
  const [averageConsumptionValue, setAverageConsumptionValue]
    = useState<number | undefined>(undefined);
  const averageConsumptionString =
    averageConsumptionValue !== undefined && averageConsumptionValue !== null
      ? averageConsumptionValue.toString() : "";
  const [incorrectQuantity, setIncorrectQuantity] = useState(false);
  const [incorrectDate, setIncorrectDate] = useState(false);

  const close = () => {
    dispatch(setResourceChargePopoverState({ isOpen: false }));
    reset();
  };

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm<ICreateChargeRequestParams>();

  useEffect(() => {
    if (resourceDetails) {
      setChargeReportSequence((resourceDetails.resource.chargeReportSequence | 0) + 1);
      const newAverageConsumption = resourceDetails.resource?.averageConsumptionOverride
        ?? resourceDetails.latestAverageConsumption?.averageConsumption;
      setAverageConsumptionValue(!Number.isNaN(newAverageConsumption) ? newAverageConsumption : undefined);
    }
  }, [resourceDetails]);

  const onChangeUsage = (newUsage: number) => {
    setUsageEntered(newUsage);
    if (resourceDetails && clickedTimes > 0) {
      setIncorrectUsage(newUsage !== resourceDetails.resource.lastKnownUsageAmount);
    }
  }

  const onChangeQuantity = (newQuantity: number) => {
    setQuantityEntered(newQuantity);
    if (resourceDetails && clickedTimes > 0) {
      setIncorrectQuantity(newQuantity > resourceDetails.resource.remainingQuantityAmount || newQuantity < 1);
    }
  }

  const onChangeChargeReportSequence = (newSequence: number) => {
    setChargeReportSequence(newSequence);
  }

  const onChangeDate = (newDate: string | null) => {
    setIncorrectDate(!isDateValid(newDate));
  }

  const isDateValid = (date: string | null) => {
    return date !== null && DiffDays(date) <= 1;
  }

  const onSubmit = async (data: ICreateChargeRequestParams) => {
    if (resourceDetails) {
      if (resourceDetails.resource.remainingQuantityAmount === 0) {
        toast.error("Sve količine resursa su već zadužene. Morate ga prvo razdužiti.");
        return;
      }
      if (haveUsage(resourceDetails.resource.category) && resourceDetails.resource.lastKnownUsageAmount !== usageEntered) {
        setIncorrectUsage(true);
        toast.error(`Unesite ${resourceDetails?.resource.usageUnit.name === "km"
          ? "tačnu kilometražu"
          : "tačne radne sate"} kao poslednje unetu (${FormatNumber(resourceDetails.resource.lastKnownUsageAmount)} ${resourceDetails.resource.usageUnit.name})!`);
        return;
      }
      if (!haveAverageConsumption(resourceDetails.resource.category) && (quantityEntered < 1 || quantityEntered > resourceDetails.resource.remainingQuantityAmount)) {
        setIncorrectQuantity(true);
        toast.error(`Unesite za količinu resursa koju zadužujete broj između 1 i ${resourceDetails.resource.remainingQuantityAmount}!`);
        return;
      }
      if (!isDateValid(data.date)) {
        setIncorrectDate(true);
        toast.error("Datum ne sme biti posle sutrašnjeg dana");
        return;
      }
      try {
        const chargeResponse = await resourcesServices.createCharge(
          resourceDetails.resource.resourceId,
          data
        );
        data.usageAmount
          && dispatch(updateLastKnownUsageAmount(data.usageAmount));
        dispatch(updateResourceCharged(chargeResponse));
        toast.success("Zaduženje je uspesno dodato.");
        close();
        onCharge();
      } catch (err) {
        toast.error("Neočekivana greška, proverite da li ste sve dobro uneli.");
        console.error(err);
      }
    }
  };

  if (!resourceDetails) return null;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`resourceChargeModal ${classes.basicPopover}`}
    >
      <div className={`${classes.row}
        ${errors.employeeId ? classes.itemError : ""}`}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>ime</label>
          <Controller
            name="employeeId"
            control={control}
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <EmployeeSelect
                customStyles={styles.customSolidBorder}
                error={errors.employeeId}
                options={employees}
                onChange={onChange}
              />
            )}
          />
        </div>
      </div>

      <div className={`${classes.row}`}>
        <div className={`${classes.col} ${classes.colDir} 
          ${errors.projectId ? classes.itemError : ""}`}>
          <label className={classes.aboveLabel}>gde je resurs</label>
          <Controller
            name="projectId"
            control={control}
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <ProjectSelect
                error={errors.projectId}
                customStyles={styles.customSolidBorder}
                onChange={onChange}
              />
            )}
          />
        </div>
      </div>

      <div className={`${classes.row} ${classes.rowDir}`}>
        {haveUsage(resourceDetails.resource.category) && (
          <div className={`${classes.col} ${classes.colDir}`}>
            <label className={classes.aboveLabel}>jed mere</label>
            <input
              className={`${classes.itemBorder} ${classes.center} ${classes.textMedium}`}
              type="text"
              value={
                resourceDetails && unitName(resourceDetails?.resource.usageUnit)
              }
              readOnly
              disabled
            />
          </div>
        )}
        {haveUsage(resourceDetails.resource.category) && (
          <div className={`${classes.col} ${classes.colDir}`}>
            <label className={classes.aboveLabel}>
              {unitNameLabel(resourceDetails?.resource.usageUnit)}
            </label>
            <Controller
              name="usageAmount"
              control={control}
              defaultValue={undefined}
              rules={{
                required: true,
              }}
              render={({ field: { onChange } }) => (
                <NumberInput
                  formatAs="quantity"
                  onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                    onChange({ target: { value: parsedValue } });
                    onChangeUsage(parsedValue);
                  }}
                  placeholder={resourceDetails.resource?.lastKnownUsageAmount !== undefined
                    ? FormatNumber(resourceDetails.resource?.lastKnownUsageAmount)
                    : undefined}
                  className={`${classes.itemBorder} ${classes.textMedium} ${classes.center}
                ${errors.usageAmount || incorrectUsage === true ? classes.itemError : ""}`}
                />
              )}
            />
          </div>
        )}
      </div>

      <div className={`${classes.row}
        ${errors.note ? classes.itemError : ""}`}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>napomena</label>
          <textarea
            className={classes.solidBorder}
            {...register("note")}
          ></textarea>
        </div>
      </div>

      <div className={`${classes.row} ${classes.rowDir}`}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>broj izveštaja</label>
          <div className={`${classes.col}`}>
            <Controller
              name="chargeReportSequence"
              control={control}
              defaultValue={chargeReportSequence}
              rules={{
                required: true,
              }}
              render={({ field: { onChange } }) => (
                <NumberInput
                  formatAs="quantity"
                  onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                    onChange({ target: { value: parsedValue } });
                    onChangeChargeReportSequence(parsedValue);;
                  }}
                  value={chargeReportSequenceString}
                  disabled={!hasFullAccessTo(user, RESOURCES_MODULE_NAME)}
                  className={`${classes.itemBorder} ${classes.center} ${classes.textMedium}
                      ${errors.quantityAmount || incorrectQuantity === true ? classes.itemError : ""}`}
                />
              )}
            />

          </div>
        </div>

        {!haveAverageConsumption(resourceDetails.resource.category) && (
          <div className={`${classes.col} ${classes.colDir}`}>
            <label className={classes.aboveLabel}>napravi revers</label>
            <input
              type="checkbox"
              className={classes.solidBorder}
              {...register("isForRevers")}
            />
          </div>
        )}
      </div>

      <div className={`${classes.row}
        ${errors.date || incorrectDate ? classes.itemError : ""}`}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>datum</label>
          <Controller
            name="date"
            control={control}
            defaultValue={new Date().toISOString().split("T")[0]}
            render={({ field: { onChange, value } }) => (
              <div>
                <DatePicker
                  onChange={(date) => {
                    const dateAsString = date ? date.toISOString().split("T")[0] : null;
                    onChange(dateAsString);
                    onChangeDate(dateAsString);
                  }}
                  showMonthDropdown
                  showYearDropdown
                  dateFormat="dd.MM.yyyy"
                  selected={value ? new Date(value) : new Date()}
                  className={`${classes.dateItem} ${classes.itemBorder}
                      ${errors.date || incorrectDate ? classes.itemError : ""}`}
                />
              </div>
            )}
          />
        </div>
      </div>

      <div className={`${classes.row} ${classes.rowDir}`}>
        {haveAverageConsumption(resourceDetails.resource.category) && (
          <div>
            <div className={`${classes.col} ${classes.colDir}`}>
              <label className={classes.aboveLabel}>prosečna potrošnja</label>
              <div className={`${classes.col}`}>
                <Controller
                  name="averageConsumption"
                  control={control}
                  defaultValue={averageConsumptionValue}
                  rules={{
                    required: true,
                  }}
                  render={({ field: { onChange } }) => (
                    <NumberInput
                      formatAs="quantity"
                      onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                        onChange({ target: { value: parsedValue } });
                        setAverageConsumptionValue(parsedValue);
                      }}
                      value={averageConsumptionString}
                      className={`${classes.itemBorder} ${classes.center} ${classes.textMedium}
                      ${errors.averageConsumption ? classes.itemError : ""}`}
                    />
                  )}
                />
                <span className={classes.suffix}>
                  {resourceDetails.resource.usageUnit &&
                    averageConsumptionSuffix(
                      resourceDetails.resource.usageUnit
                    )}
                </span>
              </div>
            </div>
          </div>
        )}

        {!haveAverageConsumption(resourceDetails.resource.category) && (
          <div>
            <div className={`${classes.col} ${classes.colDir}`}>
              <label className={classes.aboveLabel}>količina izdajem</label>
              <div className={`${classes.col}`}>
                <Controller
                  name="quantityAmount"
                  control={control}
                  defaultValue={undefined}
                  rules={{
                    required: true,
                  }}
                  render={({ field: { onChange } }) => (
                    <NumberInput
                      formatAs="quantity"
                      onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                        onChange({ target: { value: parsedValue } });
                        onChangeQuantity(parsedValue);
                      }}
                      placeholder={"1"}
                      className={`${classes.itemBorder} ${classes.center} ${classes.textMedium}
                      ${errors.quantityAmount || incorrectQuantity === true ? classes.itemError : ""}`}
                    />
                  )}
                />

              </div>
            </div>
          </div>
        )}

        <div className={`${classes.buttons} ${classes.bottom}`}>
          <ToolboxButton
            submitButton={true}
            icon={ToolboxButtonIcon.ArrowCircleDown}
            onClick={() => clickedTimes === 0
              ? setClickedTimes(clickedTimes + 1) : undefined}
          />
        </div>
      </div>
    </form>
  );
};

export default ResourceChargePopover;
